import React, { useEffect, useState, useMemo } from 'react';
import TableBuilder, { ApiRequestBuilderV2, ToastBuilder, ButtonBuilder, LabelWithSubtext, ModalLinkBuilder } from '../../../../../../GlobalComponents/Builders';
import useConfirmModal from '../../../../../../GlobalComponents/Builders/ConfirmModal';
import { Row, Col, CardBody, CardHeader, Card } from 'reactstrap';
import { H4 } from '../../../../../../AbstractElements';
import { showRelativeTime, jsonPre } from '../../../../../../GlobalComponents/Helpers';

const DMTStagerExecutions = () => {

    document.title = 'DMT Stager Executions';

    const [executions, setExecutions] = useState([]);
    const [loading, setLoading] = useState(true);
    const { confirm, ConfirmModal } = useConfirmModal();

    const fetchExecutions = () => {
        ApiRequestBuilderV2('dmtStagerExecutionsGet', {
            setIsProcessing: setLoading,
            toastOnError: true,
        }).then((res) => {
            setExecutions(res?.data || []);
        });
    };

    useEffect(() => {
        fetchExecutions();
    }, []);

    const handleRetry = async (row) => {
        const confirmed = await confirm(
            `Re-invoke stager for execution: ${row.execution_id}?\n\nThis will delete the stuck row and re-send the payload to the stager Lambda.`,
            { title: 'Retry Execution', confirmLabel: 'Retry', cancelLabel: 'Cancel' }
        );
        if (!confirmed) return;

        const res = await ApiRequestBuilderV2('dmtJobsJobIdActionsActionNamePost', {
            pathParams: { jobId: row.job_id, actionName: 'retry-stager-execution' },
            body: { sourceResourceId: row.resource_id, formData: { executionId: row.execution_id } },
            toastOnError: true,
        });
        if (res?.success) {
            const result = typeof res.data === 'string' ? JSON.parse(res.data) : res.data;
            if (result?.success) {
                ToastBuilder('success', result.message || 'Retry sent');
                fetchExecutions();
            } else {
                ToastBuilder('error', result?.message || 'Retry failed');
            }
        }
    };

    const getStuckDuration = (startedAt) => {
        if (!startedAt) return null;
        const now = Math.floor(Date.now() / 1000);
        const diff = now - startedAt;
        if (diff < 60) return `${diff}s`;
        if (diff < 3600) return `${Math.floor(diff / 60)}m`;
        return `${Math.floor(diff / 3600)}h ${Math.floor((diff % 3600) / 60)}m`;
    };

    const getSeverityBadge = (startedAt) => {
        if (!startedAt) return null;
        const now = Math.floor(Date.now() / 1000);
        const diff = now - startedAt;
        if (diff > 1800) return <span className="badge bg-danger">Likely Failed</span>;
        if (diff > 600) return <span className="badge bg-warning text-dark">Stale</span>;
        return <span className="badge bg-info text-dark">In-Flight</span>;
    };

    const parseExecutionId = (executionId) => {
        // Extract chunk/batch from execution_id like "DMT-Stager-208-431-Tickets-c2-b3"
        const chunkMatch = executionId.match(/-c(\d+)/);
        const batchMatch = executionId.match(/-b(\d+)/);
        return {
            chunk: chunkMatch ? parseInt(chunkMatch[1]) : 0,
            batch: batchMatch ? parseInt(batchMatch[1]) : null,
        };
    };

    const summary = useMemo(() => {
        const now = Math.floor(Date.now() / 1000);
        const total = executions.length;
        const likelyFailed = executions.filter(e => (now - e.started_at) > 1800).length;
        const inFlight = total - likelyFailed;
        return { total, likelyFailed, inFlight };
    }, [executions]);

    return (
        <>
        <ConfirmModal />
        <Row>
            <Col sm="12">
                <Card>
                    <CardHeader className="d-flex justify-content-between align-items-center pb-1">
                        <div>
                            <H4 className="mb-0">Stager Executions</H4>
                            <small className="text-muted">
                                {summary.total === 0
                                    ? 'All clear — no in-flight or stuck executions'
                                    : `${summary.total} execution(s) tracked — ${summary.inFlight} in-flight, ${summary.likelyFailed} likely failed`
                                }
                            </small>
                        </div>
                        <ButtonBuilder label="Refresh" onClick={fetchExecutions} color={null} className="btn-secondary" size="sm" useSpinner={false} />
                    </CardHeader>
                    <CardBody>
                        <TableBuilder
                            data={executions}
                            processing={loading}
                            pagination
                            paginationPerPage={25}
                            sortField={'started_at'}
                            sortOrderAsc={false}
                            subHeader={true}
                            columnsToSearch={['execution_id', 'job_id', 'resource_id', 'job_name', 'resource_name']}
                            columns={[
                                {
                                    name: 'Job',
                                    selector: (row) => row.job_id,
                                    sortable: true,
                                    center: true,
                                    cell: (row) => (
                                        <LabelWithSubtext
                                            text={row.job_id}
                                            subtext={row.job_name}
                                            align='center'
                                        />
                                    ),
                                },
                                {
                                    name: 'Resource',
                                    selector: (row) => row.resource_id,
                                    sortable: true,
                                    center: true,
                                    cell: (row) => (
                                        <LabelWithSubtext
                                            text={row.resource_id}
                                            subtext={row.resource_name}
                                            align='center'
                                        />
                                    ),
                                },
                                {
                                    name: 'Execution',
                                    selector: (row) => row.execution_id,
                                    sortable: true,
                                    cell: (row) => {
                                        const { chunk, batch } = parseExecutionId(row.execution_id);
                                        return (
                                            <div>
                                                <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                                                    {chunk > 0 ? `c${chunk}` : ''}{batch ? `-b${batch}` : ''}
                                                </span>
                                                <br />
                                                <span style={{ fontSize: '10px', color: '#999' }}>{row.execution_id}</span>
                                            </div>
                                        );
                                    },
                                },
                                {
                                    name: 'Started',
                                    selector: (row) => row.started_at,
                                    sortable: true,
                                    center: true,
                                    cell: (row) => showRelativeTime(row.started_at),
                                    sortFunction: (a, b) => a.started_at - b.started_at,
                                },
                                {
                                    name: 'Duration',
                                    center: true,
                                    cell: (row) => (
                                        <span style={{ fontFamily: 'monospace' }}>
                                            {getStuckDuration(row.started_at)}
                                        </span>
                                    ),
                                },
                                {
                                    name: 'Status',
                                    center: true,
                                    cell: (row) => getSeverityBadge(row.started_at),
                                },
                                {
                                    name: 'Payload',
                                    center: true,
                                    cell: (row) => {
                                        const payload = typeof row.payload === 'string' ? JSON.parse(row.payload) : row.payload;
                                        return (
                                            <ModalLinkBuilder
                                                onSubmit={false}
                                                onCancelLabel='Close'
                                                label={<span style={{ color: '#1976d2', cursor: 'pointer', fontSize: 12 }}>View</span>}
                                                title={`Payload — ${row.execution_id}`}
                                                body={jsonPre(payload)}
                                                size='lg'
                                            />
                                        );
                                    },
                                },
                                {
                                    name: '',
                                    center: true,
                                    cell: (row) => (
                                        <div className="d-flex gap-1">
                                            <ButtonBuilder
                                                label="Retry"
                                                className="btn-danger"
                                                size="sm"
                                                useSpinner={false}
                                                onClick={() => handleRetry(row)}
                                            />
                                            <ButtonBuilder
                                                label="Progress"
                                                className="btn-outline-primary"
                                                size="sm"
                                                useSpinner={false}
                                                onClick={() => window.open(`${process.env.PUBLIC_URL}/tools/data-migration/jobs/${row.job_id}/progress`, '_blank')}
                                            />
                                        </div>
                                    ),
                                },
                            ]}
                        />
                    </CardBody>
                </Card>
            </Col>
        </Row>
        </>
    );
};

export default DMTStagerExecutions;
